import React from "react";
import styled, { css } from "styled-components";

import { shadows, theme } from "../styles/theme";
import { SwitchContainer, SwitchLabel } from "./buttons/switch-button";
import { ChevronIcon } from "./svg/chevron";

/** The redesign's list card: rows stacked in a white panel and parted by hairlines. */
export const ActionRowList = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  ${shadows.sm};
`;

export const actionRowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: 0;
  border-top: 1px solid ${theme.colors.gray[100]};
  border-radius: 0;
  background-color: transparent;
  text-align: left;
  cursor: pointer;

  &:first-child {
    border-top: 0;
  }

  &:hover {
    background-color: ${theme.colors.gray[50]};
  }

  &:disabled {
    cursor: default;
    opacity: 0.3;
  }
`;

interface ActionRowProps {
  label: string;
  subtitle?: string;
  /** Artwork announcing the action, as the card rows carry; rows without one start at the label. */
  iconSrc?: string;
  /** A trailing image for the actions that act in place; navigating rows get a chevron instead. */
  trailingIconSrc?: string;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
}

export function ActionRow(props: Readonly<ActionRowProps>) {
  const { label, subtitle, iconSrc, trailingIconSrc, disabled, className, onClick } = props;

  return (
    <Row type="button" className={className} disabled={disabled} onClick={onClick}>
      {!!iconSrc && <LeadingIcon src={iconSrc} alt="" />}
      <Texts>
        <Label>{label}</Label>
        {!!subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Texts>
      {trailingIconSrc ? <TrailingIcon src={trailingIconSrc} alt="" /> : <RowChevron width={14} height={14} />}
    </Row>
  );
}

interface ToggleRowProps {
  label: string;
  value: boolean;
  disabled?: boolean;
  className?: string;
  onSwitch: (value: boolean) => void;
}

/** A row whose action is a switch rather than a destination, so it carries no chevron. */
export function ToggleRow(props: Readonly<ToggleRowProps>) {
  const { label, value, disabled, className, onSwitch } = props;

  return (
    <SwitchRow
      className={className}
      $disabled={disabled}
      value={value}
      getSwitchLabel={() => label}
      onSwitch={(next) => !disabled && onSwitch(next)}
    />
  );
}

const Row = styled.button`
  ${actionRowStyle};
`;

/* SwitchContainer already lays a label out against a switch; only the row chrome is missing. */
const SwitchRow = styled(SwitchContainer)<{ $disabled?: boolean }>`
  ${actionRowStyle};
  gap: 12px;

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: default;
      opacity: 0.3;
    `};

  ${SwitchLabel} {
    ${theme.text};
    font-size: 16px;
    line-height: 22px;
    color: ${theme.colors.blue[800]};
  }
`;

const Texts = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const Label = styled.span`
  ${theme.text};
  font-size: 16px;
  line-height: 22px;
  color: ${theme.colors.blue[800]};
`;

const Subtitle = styled.span`
  ${theme.text};
  margin-top: 2px;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.gray[800]};
`;

const LeadingIcon = styled.img`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
`;

const TrailingIcon = styled.img`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const RowChevron = styled(ChevronIcon)`
  flex-shrink: 0;
  fill: ${theme.colors.blue[800]};
`;
